import { useState, useEffect } from 'react'
import { useTheme } from './ThemeProvider'

interface BurningPaperProps {
  text: string
  onComplete: () => void
  onCancel: () => void
}

const BurningPaper = ({ text, onComplete, onCancel }: BurningPaperProps) => {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [burnProgress, setBurnProgress] = useState(0)
  const [isStarted, setIsStarted] = useState(false)
  const [showConfirm, setShowConfirm] = useState(true)

  useEffect(() => {
    if (!isStarted) return

    const duration = 4000 // 4 seconds burn time
    const interval = 50 // Update every 50ms
    const steps = duration / interval
    const increment = 100 / steps

    const timer = setInterval(() => {
      setBurnProgress(prev => {
        const next = prev + increment
        if (next >= 100) {
          clearInterval(timer)
          setTimeout(onComplete, 500) // Small delay before completing
          return 100
        }
        return next
      })
    }, interval)

    return () => clearInterval(timer)
  }, [isStarted, onComplete])

  const startBurning = () => {
    setShowConfirm(false)
    setIsStarted(true)
  }

  // Calculate how much text to show based on burn progress
  const getVisibleText = () => {
    if (!isStarted) return text
    const visibleLength = Math.floor(text.length * (1 - burnProgress / 100))
    return text.substring(0, visibleLength)
  }

  // Get burn effect styles
  const getBurnStyles = () => {
    if (!isStarted) return {}
    
    const burnHeight = burnProgress
    return {
      background: `linear-gradient(to bottom, 
        rgba(255, 255, 255, 0.9) 0%, 
        rgba(255, 255, 255, 0.9) ${100 - burnHeight}%, 
        rgba(255, 140, 0, 0.8) ${100 - burnHeight}%, 
        rgba(255, 69, 0, 0.9) ${100 - burnHeight + 5}%, 
        rgba(139, 69, 19, 0.7) ${100 - burnHeight + 10}%, 
        rgba(0, 0, 0, 0.8) 100%)`,
      clipPath: `polygon(0 0, 100% 0, 100% ${100 - burnProgress}%, 0 ${100 - burnProgress}%)`
    }
  }

  const getFlameEffect = () => {
    if (!isStarted || burnProgress < 10) return null
    
    return (
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center ${100 - burnProgress}%, 
            rgba(255, 140, 0, 0.6) 0%, 
            rgba(255, 69, 0, 0.4) 30%, 
            transparent 60%)`,
          animation: 'flicker 0.5s ease-in-out infinite alternate'
        }}
      />
    )
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 sm:p-8">
        <div className="bg-cream-25 dark:bg-sage-800 rounded-3xl p-6 sm:p-8 max-w-md w-full relative overflow-hidden">
          {showConfirm ? (
            // Confirmation screen
            <div className="text-center">
              <div className="text-xl sm:text-2xl mb-4">🔥</div>
              <h3 className="text-cream-600 dark:text-cream-300 font-light text-base sm:text-lg mb-4">
                Burn this entry?
              </h3>
              <p className="text-cream-500 dark:text-cream-400 font-light text-xs sm:text-sm mb-6 sm:mb-8 leading-relaxed">
                Once burned, this entry will be gone forever. This can be a gentle way to let go.
              </p>
              
              {/* Preview of text to be burned */}
              <div className="bg-cream-50 dark:bg-sage-700 p-3 sm:p-4 rounded-2xl border border-cream-100/50 dark:border-sage-600/50 mb-6 sm:mb-8 max-h-32 overflow-hidden">
                <p className="text-cream-600 dark:text-cream-300 font-light text-xs sm:text-sm leading-relaxed break-words">
                  {text.length > 150 ? `${text.substring(0, 150)}...` : text}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={onCancel}
                  className="flex-1 px-4 sm:px-6 py-3 bg-cream-100/60 hover:bg-cream-200/50 
                             dark:bg-cream-900/30 dark:hover:bg-cream-800/40
                             text-cream-600 dark:text-cream-300 rounded-2xl text-xs sm:text-sm font-light 
                             border border-cream-200/40 dark:border-cream-700/30 
                             hover:border-cream-300/50 dark:hover:border-cream-600/40
                             transition-colors duration-300 focus:outline-none focus:ring-4 
                             focus:ring-cream-100 dark:focus:ring-cream-700/30"
                >
                  Keep it
                </button>
                <button
                  onClick={startBurning}
                  className="flex-1 px-4 sm:px-6 py-3 bg-peach-100/60 hover:bg-peach-200/50 
                             dark:bg-peach-900/30 dark:hover:bg-peach-800/40
                             text-peach-600 dark:text-peach-300 rounded-2xl text-xs sm:text-sm font-light 
                             border border-peach-200/40 dark:border-peach-700/30 
                             hover:border-peach-300/50 dark:hover:border-peach-600/40
                             transition-colors duration-300 focus:outline-none focus:ring-4 
                             focus:ring-peach-100 dark:focus:ring-peach-700/30"
                >
                  Burn it
                </button>
              </div>
            </div>
          ) : (
            // Burning animation
            <div className="text-center">
              <div className="text-xl sm:text-2xl mb-4">🔥</div>
              <h3 className="text-cream-600 dark:text-cream-300 font-light text-base sm:text-lg mb-6">
                Letting go...
              </h3>
              
              {/* Burning paper effect */}
              <div className="relative bg-white dark:bg-sage-700 rounded-2xl border border-cream-200/50 dark:border-sage-600/50 p-4 sm:p-6 mb-4 sm:mb-6 min-h-32 overflow-hidden">
                {/* Paper background with burn effect */}
                <div 
                  className="absolute inset-0 rounded-2xl"
                  style={getBurnStyles()}
                />
                
                {/* Flame effect */}
                {getFlameEffect()}
                
                {/* Text content */}
                <div className="relative z-10">
                  <p className="text-cream-600 dark:text-cream-300 font-light text-xs sm:text-sm leading-relaxed break-words">
                    {getVisibleText()}
                  </p>
                </div>
                
                {/* Burning edge effect */}
                {isStarted && (
                  <div 
                    className="absolute left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-red-500 to-orange-400"
                    style={{
                      top: `${100 - burnProgress}%`,
                      opacity: burnProgress > 5 ? 1 : 0,
                      boxShadow: '0 0 10px rgba(255,69,0,0.8)',
                      animation: 'flicker 0.3s ease-in-out infinite alternate'
                    }}
                  />
                )}
              </div>

              {/* Progress indicator */}
              <div className="w-full bg-cream-100 dark:bg-sage-700 rounded-full h-2 mb-4">
                <div 
                  className="bg-gradient-to-r from-orange-400 to-red-500 h-2 rounded-full transition-all duration-100"
                  style={{ width: `${burnProgress}%` }}
                />
              </div>

              <p className="text-cream-500 dark:text-cream-400 font-light text-xs">
                {burnProgress < 100 ? 'Burning...' : 'Released'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* CSS for flame flicker animation */}
      <style>{`
        @keyframes flicker {
          0% { opacity: 0.8; transform: scale(1); }
          100% { opacity: 1; transform: scale(1.05); }
        }
      `}</style>
    </>
  )
}

export default BurningPaper